import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { authUserMetaAtom, clearDelegateViewingAtom } from '@/store/authAtoms';
import { navAccountLoginActiveAtom } from '@/store/navAccountLoginAtom';
import { setCsrfToken } from '@/lib/csrf';
import { authService } from '@/services/authService';
import { useContent } from '@/hooks/useContent';
import { trackAction, trackLoginSuccess } from '@/observability/actions';

const loginSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});
type LoginFormData = z.infer<typeof loginSchema>;

function parseLoginErrorCode(data: unknown): string | undefined {
  if (typeof data === 'string') {
    try {
      return parseLoginErrorCode(JSON.parse(data) as unknown);
    } catch {
      return undefined;
    }
  }
  if (!data || typeof data !== 'object') return undefined;
  const code = (data as Record<string, unknown>).code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * Compact username/password form shown in the sidebar to iCode-session patients who already
 * have a portal account (ICodeSession.hasAccount) — lets them trade the restricted iCode
 * session for a full one without leaving the current page. MFA / pending-terms accounts are
 * handed off to the dedicated /sign-in page with enough resume state to continue without
 * re-entering the password.
 */
export default function NavAccountLogin() {
  const setMeta = useSetAtom(authUserMetaAtom);
  const clearDelegateViewing = useSetAtom(clearDelegateViewingAtom);
  const setFormActive = useSetAtom(navAccountLoginActiveAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const { getContent, getContentString } = useContent();
  const [serverErr, setServerErr] = useState<string | null>(null);

  // Reset on unmount — covers a successful login (component disappears once the session is
  // full) and any other reason the form goes away, so AppLayout doesn't get stuck ignoring
  // mouse-leave forever.
  useEffect(() => () => setFormActive(false), [setFormActive]);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data: LoginFormData) => {
    setServerErr(null);
    trackAction('auth.login_start', { auth_method: 'password' });
    try {
      const result = await authService.login({ username: data.username, password: data.password });

      // Return to the page the patient was already on — for the plain-success path below,
      // and (via SignIn's own getPostAuthRedirect(state.from)) after an MFA/terms hand-off too.
      const returnTo = { pathname: location.pathname, search: location.search };

      // Terms-before-MFA (or terms-only): login returns setupToken without an mfaToken.
      // Must hand off the setupToken — prefillUsername alone drops the gate and looks like a failed login.
      if (result.requiresTermsAcceptance && result.setupToken) {
        const termsFlow =
          result.mfaSetupRequired && !result.mfaRequired
            ? 'setup'
            : result.mfaRequired
              ? 'mfaChallenge'
              : 'login';
        navigate('/sign-in', {
          state: {
            termsResume: {
              setupToken: result.setupToken,
              termsFlow,
            },
            prefillUsername: data.username,
            from: returnTo,
          },
        });
        return;
      }

      if (result.mfaRequired && result.mfaToken) {
        // Don't duplicate the MFA UI here — hand off to the full sign-in page, carrying the
        // challenge context so it resumes the MFA prompt directly (no password re-entry). The
        // mfaToken is a short-lived, API-scopeless challenge token, safe to pass in router state.
        navigate('/sign-in', {
          state: {
            mfaResume: {
              mfaToken:   result.mfaToken,
              mfaMethod:  result.mfaMethod ?? null,
              mfaMethods: result.mfaMethods ?? null,
            },
            from: returnTo,
          },
        });
        return;
      }

      if (result.mfaSetupRequired && result.setupToken) {
        navigate('/sign-in', {
          state: {
            setupResume: { setupToken: result.setupToken },
            prefillUsername: data.username,
            from: returnTo,
          },
        });
        return;
      }

      if (result.csrfToken) setCsrfToken(result.csrfToken);
      clearDelegateViewing();
      const userId = String(result.userKey);
      setMeta({
        id: userId,
        fullName: result.userName,
        roles: ['patient'],
        passwordMustBeReset:
          result.passwordResetRequired === true || Boolean(result.resetReason),
      });
      trackLoginSuccess({ auth_method: 'password', mfa_used: false, user_id: userId });
      navigate(`${returnTo.pathname}${returnTo.search}`, { replace: true });
    } catch (err: unknown) {
      const code = parseLoginErrorCode((err as { response?: { data?: unknown } })?.response?.data) ?? 'invalidCredentials';
      trackAction('auth.login_failure', { failure_reason: code });
      const paragraphCode =
        code === 'accountLocked'   ? 'PPLoginAccountLocked'   :
        code === 'accountDisabled' ? 'PPLoginAccountDisabled' :
        code === 'rateLimited'     ? 'PPLoginRateLimited'     :
        'PPInvalidCredentialsMessage';
      setServerErr(getContentString(paragraphCode) || getContentString('PPInvalidCredentialsMessage'));
      // Keep the sidebar expanded so the error stays visible — otherwise a hover-expanded
      // sidebar re-collapses on blur and unmounts this form, discarding the message.
      setFormActive(true);
    }
  };

  return (
    <Box
      sx={{ px: 0.5, mt: 0.5, mb: 0.5 }}
      onFocus={() => setFormActive(true)}
      onBlur={(e) => {
        // Keep the form pinned open while an error is showing so it isn't collapsed away.
        if (serverErr) return;
        // Only clear once focus actually leaves this whole form, not when it moves from
        // one field to another inside it.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFormActive(false);
      }}
    >
      <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', mb: 0.75 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}
        >
          {getContent('PPLoginSignIn')}
        </Typography>
        <Tooltip title={getContentString('PPNavAccountLoginHint')} arrow enterTouchDelay={0} leaveTouchDelay={4000}>
          <IconButton size="small" sx={{ p: 0.25 }} aria-label={getContentString('PPNavAccountLoginHint')}>
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={0.75}>
          {serverErr && (
            <Typography variant="caption" color="error.main" sx={{ fontSize: '0.6875rem', lineHeight: 1.3 }}>
              {serverErr}
            </Typography>
          )}
          <Controller
            name="username"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                placeholder={getContentString('PPAccountPasswordUserId')}
                aria-label={getContentString('PPAccountPasswordUserId')}
                disabled={isSubmitting}
                fullWidth
                slotProps={{ htmlInput: { style: { fontSize: '0.8125rem' } } }}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                type="password"
                size="small"
                placeholder={getContentString('PPLoginPassword')}
                aria-label={getContentString('PPLoginPassword')}
                disabled={isSubmitting}
                fullWidth
                slotProps={{ htmlInput: { style: { fontSize: '0.8125rem' } } }}
              />
            )}
          />
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={12} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, fontSize: '0.75rem', textTransform: 'none', fontWeight: 600, py: 0.6 }}
          >
            {isSubmitting ? getContent('PPLoginSigningIn') : getContent('PPLoginSignIn')}
          </Button>
        </Stack>
      </form>
    </Box>
  );
}
